/**
 * Project generator
 */

import { join } from 'path';
import { exec } from 'child_process';
import { promisify } from 'util';
import type { ProjectConfig } from './types.js';
import { generateProjectFiles } from './templates.js';
import { generateIDEPreset } from './ide-presets.js';
import { directoryExists, writeFiles } from './fs-utils.js';

const execAsync = promisify(exec);

/**
 * Generate a new project
 */
export async function generateProject(config: ProjectConfig): Promise<void> {
  const targetDir = config.directory || config.name;
  const fullPath = join(process.cwd(), targetDir);

  // Check if directory already exists
  if (await directoryExists(fullPath)) {
    throw new Error(`Directory already exists: ${targetDir}`);
  }

  // Generate project files
  const projectFiles = generateProjectFiles(config);
  const ideFiles = generateIDEPreset(config.ide);
  const allFiles = [...projectFiles, ...ideFiles];

  // Write files
  await writeFiles(fullPath, allFiles);

  // Initialize git (unless skipped)
  if (!config.skipGit) {
    try {
      await execAsync('git init', { cwd: fullPath });
      await execAsync('git add .', { cwd: fullPath });
      await execAsync('git commit -m "Initial commit from @thenoo/cli"', {
        cwd: fullPath,
      });
    } catch (error) {
      // Git init failed, but continue
      console.warn('⚠️ Failed to initialize git repository');
    }
  }

  // Install dependencies (unless skipped)
  if (!config.skipInstall) {
    try {
      console.log('📦 Installing dependencies...');
      await execAsync('npm install', { cwd: fullPath });
    } catch (error) {
      console.warn('⚠️ Failed to install dependencies. Run "npm install" manually.');
    }
  }
}

/**
 * Get next steps message
 */
export function getNextSteps(config: ProjectConfig): string {
  const dir = config.directory || config.name;
  const lines: string[] = [
    '',
    '✅ Project created successfully!',
    '',
    'Next steps:',
    `  cd ${dir}`,
  ];

  if (config.skipInstall) {
    lines.push('  npm install');
  }

  if (config.adapter === 'openai') {
    lines.push('  echo "OPENAI_API_KEY=your_key" > .env');
  } else if (config.adapter === 'azure') {
    lines.push('  echo "AZURE_OPENAI_KEY=your_key" > .env');
    lines.push('  echo "AZURE_OPENAI_ENDPOINT=your_endpoint" >> .env');
  }

  lines.push('  npm start');
  lines.push('');
  lines.push('📚 Documentation: https://github.com/ZacheryKuykendall/noo');
  lines.push('');

  return lines.join('\n');
}

